import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Button,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Build as BuildIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { apiClient } from '../services/apiClient';

const WebhookMonitoring = () => {
  const [metrics, setMetrics] = useState(null);
  const [reconciliation, setReconciliation] = useState(null);
  const [failedWebhooks, setFailedWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fixingIssues, setFixingIssues] = useState(false);
  const [showFailedDialog, setShowFailedDialog] = useState(false);
  const [bulkRetrying, setBulkRetrying] = useState(false);
  const [memoryInfo, setMemoryInfo] = useState(null);
  const [cleanupStats, setCleanupStats] = useState(null);

  // Função auxiliar para calcular taxa de sucesso corretamente
  const calculateSuccessRate = (received, processed, failed) => {
    // Converter para números e garantir que não são negativos
    const r = Math.max(0, Number(received) || 0);
    const p = Math.max(0, Number(processed) || 0);
    const f = Math.max(0, Number(failed) || 0);
    
    // Se não há dados, considerar 100% de sucesso
    if (r === 0 && p === 0 && f === 0) return 100;
    
    // Se há dados de recebidos, usar como base
    if (r > 0) {
      const successful = Math.max(0, r - f);
      return (successful / r) * 100;
    }
    
    // Se só há dados de processados sem falhados, considerar 100%
    if (p > 0 && f === 0) return 100;
    
    // Se há falhados sem processados, considerar 0%
    if (f > 0 && p === 0) return 0;
    
    // Fallback para casos não cobertos
    return p > 0 ? Math.max(0, ((p - f) / p) * 100) : 0;
  };

  // Função auxiliar para formatar porcentagem
  const formatPercentage = (value) => {
    const num = Number(value);
    if (isNaN(num) || !isFinite(num)) return '0';
    return Math.round(Math.max(0, Math.min(100, num))); // Entre 0 e 100
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [metricsRes, reconciliationRes, failedRes, memoryRes] = await Promise.allSettled([
        apiClient.get('/api/v1/webhook/metrics'),
        apiClient.get('/api/v1/webhook/reconciliation'),
        apiClient.get('/api/v1/webhook/failed?limit=20'),
        apiClient.get('/api/v1/webhook/memory/usage')
      ]);

      // Tratar cada resposta individualmente para evitar falha total se uma API falhar
      if (metricsRes.status === 'fulfilled') {
        setMetrics(metricsRes.value.data.data);
      } else {
        console.warn('Erro ao carregar métricas:', metricsRes.reason);
        setMetrics({}); // Dados padrão vazios
      }

      if (reconciliationRes.status === 'fulfilled') {
        setReconciliation(reconciliationRes.value.data.data);
      } else {
        console.warn('Erro ao carregar reconciliação:', reconciliationRes.reason);
        setReconciliation({});
      }

      if (failedRes.status === 'fulfilled') {
        setFailedWebhooks(failedRes.value.data.data?.failed_webhooks || []);
      } else {
        console.warn('Erro ao carregar webhooks falhados:', failedRes.reason);
        setFailedWebhooks([]);
      }

      if (memoryRes.status === 'fulfilled') {
        setMemoryInfo(memoryRes.value.data.data);
      } else {
        console.warn('Erro ao carregar informações de memória:', memoryRes.reason);
        // Não definir memoryInfo como vazio para manter comportamento condicional
      }

    } catch (error) {
      console.error('Erro geral ao carregar dados de monitoramento:', error);
    }
    setLoading(false);
  };

  const performCleanup = async (force = false) => {
    try {
      const response = await apiClient.post(`/api/v1/webhook/cleanup/redis?force=${force}`);
      if (response.data.success) {
        setCleanupStats(response.data.data.cleanup_stats);
        await loadData(); // Recarregar dados após limpeza
      }
    } catch (error) {
      console.error('Erro na limpeza:', error);
    }
  };

  const fixIssues = async () => {
    setFixingIssues(true);
    try {
      const response = await apiClient.post('/api/v1/webhook/reconciliation/fix');
      if (response.data.success) {
        await loadData(); // Recarregar dados após correção
        alert('Correções aplicadas com sucesso!');
      }
    } catch (error) {
      console.error('Erro ao corrigir issues:', error);
      alert('Erro ao aplicar correções. Verifique os logs.');
    }
    setFixingIssues(false);
  };

  const retryWebhook = async (jobId) => {
    try {
      const response = await apiClient.post(`/api/v1/webhook/retry/${jobId}`);
      if (response.data.success) {
        await loadData(); // Recarregar dados
        alert('Webhook reprocessado com sucesso!');
      }
    } catch (error) {
      console.error('Erro ao reprocessar webhook:', error);
      alert('Erro ao reprocessar webhook.');
    }
  };

  const retryAllFailed = async () => {
    setBulkRetrying(true);
    try {
      // Tenta endpoint principal
      let response = await apiClient.post(`/api/v1/webhook/retry/all`);
      // Se falhar com 404, tenta alias
      if (!response?.success && response?.status === 404) {
        response = await apiClient.post(`/api/v1/retry/all`);
      }
      // Alguns wrappers retornam o payload direto
      const payload = response?.data || response;
      if (payload?.success) {
        await loadData();
        alert(`${payload.count} webhooks recolocados na fila!`);
      } else {
        throw new Error(payload?.message || 'Falha no reprocessamento em massa');
      }
    } catch (error) {
      console.error('Erro ao reprocessar todos os webhooks:', error);
      const msg = error?.message || error?.data?.detail || 'Erro ao reprocessar todos os webhooks.';
      alert(msg);
    } finally {
      setBulkRetrying(false);
    }
  };

  useEffect(() => {
    loadData();
    
    // Auto-refresh a cada 30 segundos
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  const getHealthColor = (score) => {
    if (score >= 95) return 'success';
    if (score >= 85) return 'warning';
    return 'error';
  };

  const getHealthIcon = (status) => {
    switch (status) {
      case 'excellent':
        return <CheckCircleIcon color="success" />;
      case 'good':
        return <CheckCircleIcon color="info" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'critical':
        return <ErrorIcon color="error" />;
      default:
        return <InfoIcon />;
    }
  };

  const healthScore = metrics?.health_score || {};
  const queueStatus = metrics?.queue_status || {};
  const todayMetrics = metrics?.today || {};
  const aggregationIssues = reconciliation?.aggregation_integrity?.total_discrepancies || 0;
  const processingGaps = reconciliation?.processing_gaps?.total_gaps || 0;

  // Calcular métricas corretamente
  const todayReceived = todayMetrics.received || 0;
  const todayProcessed = todayMetrics.processed || 0;
  const todayFailed = todayMetrics.failed || 0;
  
  // Taxa de sucesso usando a função auxiliar
  const successRate = calculateSuccessRate(todayReceived, todayProcessed, todayFailed);
  
  // Score de saúde geral
  const overallHealthScore = healthScore.score || successRate;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          🔍 Monitoramento de Webhooks
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<AssessmentIcon />}
            onClick={() => setShowFailedDialog(true)}
            disabled={failedWebhooks.length === 0}
          >
            Ver Falhados ({failedWebhooks.length})
          </Button>
          <Button
            variant="contained"
            startIcon={<BuildIcon />}
            onClick={fixIssues}
            disabled={fixingIssues || (aggregationIssues === 0)}
            color="warning"
          >
            {fixingIssues ? 'Corrigindo...' : `Corrigir Issues (${aggregationIssues})`}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            disabled={loading}
          >
            {loading ? 'Atualizando...' : 'Atualizar'}
          </Button>
        </Box>
      </Box>

      {/* Aviso se não há dados */}
      {(!metrics || Object.keys(metrics).length === 0) && !loading && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Carregando dados...</strong> Se esta mensagem persistir, verifique se a API está respondendo corretamente.
          </Typography>
        </Alert>
      )}

      {/* Health Score Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="between" mb={2}>
            <Box display="flex" alignItems="center">
              {getHealthIcon(healthScore.status)}
              <Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
                Status de Saúde do Sistema
              </Typography>
            </Box>
            <Chip
              label={`${formatPercentage(overallHealthScore)}%`}
              color={getHealthColor(overallHealthScore)}
              size="large"
              sx={{ fontSize: '1.1rem', fontWeight: 600 }}
            />
          </Box>
          
          <LinearProgress
            variant="determinate"
            value={Math.min(overallHealthScore, 100)}
            color={getHealthColor(overallHealthScore)}
            sx={{ height: 8, borderRadius: 4, mb: 2 }}
          />
          
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">Taxa de Sucesso</Typography>
              <Typography variant="h6">{formatPercentage(successRate)}%</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">Recebidos Hoje</Typography>
              <Typography variant="h6">{todayReceived}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">Processados Hoje</Typography>
              <Typography variant="h6">{todayProcessed}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">Falhados Hoje</Typography>
              <Typography variant="h6" color={todayFailed > 0 ? 'error' : 'inherit'}>
                {todayFailed}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Memory Usage Card */}
      {memoryInfo && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="between" mb={2}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                📊 Uso de Memória Redis
              </Typography>
              <Chip
                label={`${memoryInfo.used_memory_mb || 0} MB`}
                color={memoryInfo.used_memory_mb > 200 ? 'error' : memoryInfo.used_memory_mb > 100 ? 'warning' : 'success'}
                size="large"
                sx={{ fontSize: '1rem', fontWeight: 600 }}
              />
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">Memória Usada</Typography>
                <Typography variant="h6">{memoryInfo.used_memory_human || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">Pico de Memória</Typography>
                <Typography variant="h6">{memoryInfo.used_memory_peak_mb?.toFixed(1) || 0} MB</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">Total de Chaves</Typography>
                <Typography variant="h6">{memoryInfo.keys_count || 0}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">Taxa de Fragmentação</Typography>
                <Typography variant="h6" color={memoryInfo.memory_fragmentation_ratio > 1.5 ? 'warning' : 'inherit'}>
                  {memoryInfo.memory_fragmentation_ratio?.toFixed(2) || '1.00'}
                </Typography>
              </Grid>
            </Grid>
            
            {/* <Box mt={2} display="flex" gap={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => performCleanup(false)}
                disabled={loading}
              >
                🧹 Limpeza Normal
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="warning"
                onClick={() => performCleanup(true)}
                disabled={loading}
              >
                🧹 Limpeza Forçada
              </Button>
            </Box> */}
            
            {cleanupStats && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  ✅ Limpeza concluída: {cleanupStats.failed_jobs_removed} jobs removidos, {' '}
                  {cleanupStats.old_metrics_removed} métricas antigas removidas, {' '}
                  {cleanupStats.orphaned_jobmaps} jobmaps corrigidos
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Status das Filas */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Pendentes</Typography>
              <Typography variant="h4" color="primary">{queueStatus.pending || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Processando</Typography>
              <Typography variant="h4" color={queueStatus.processing > 50 ? 'warning' : 'info'}>
                {queueStatus.processing || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Atrasados</Typography>
              <Typography variant="h4" color={queueStatus.delayed > 0 ? 'warning' : 'inherit'}>
                {queueStatus.delayed || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Falhados</Typography>
              <Typography variant="h4" color={queueStatus.failed > 0 ? 'error' : 'inherit'}>
                {queueStatus.failed || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alertas */}
      {aggregationIssues > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Discrepâncias Encontradas:</strong> {aggregationIssues} inconsistências nas agregações diárias.
            Clique em "Corrigir Issues" para resolver automaticamente.
          </Typography>
        </Alert>
      )}

      {/* {processingGaps > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Possível Perda de Dados:</strong> {processingGaps} dias com gaps no processamento de webhooks.
            Verifique os logs do sistema para mais detalhes.
          </Typography>
        </Alert>
      )} */}

      {queueStatus.processing > 100 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Fila Congestionada:</strong> {queueStatus.processing} webhooks presos em processamento.
            Pode indicar problema nos workers.
          </Typography>
        </Alert>
      )}

      {/* Métricas dos Últimos 7 Dias */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            📊 Métricas dos Últimos 7 Dias
          </Typography>
          
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell align="right">Recebidos</TableCell>
                  <TableCell align="right">Processados</TableCell>
                  <TableCell align="right">Falhados</TableCell>
                  <TableCell align="right">Taxa de Sucesso</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(metrics?.last_7_days || {})
                  .sort(([a], [b]) => b.localeCompare(a)) // Mais recentes primeiro
                  .map(([date, dayMetrics]) => {
                    const received = dayMetrics.received || 0;
                    const processed = dayMetrics.processed || 0;
                    const failed = dayMetrics.failed || 0;
                    
                    // Calcular taxa de sucesso usando função auxiliar
                    const daySuccessRate = calculateSuccessRate(received, processed, failed);
                    
                    return (
                      <TableRow key={date}>
                        <TableCell>{date}</TableCell>
                        <TableCell align="right">{received}</TableCell>
                        <TableCell align="right">{processed}</TableCell>
                        <TableCell align="right">
                          <Typography color={failed > 0 ? 'error' : 'inherit'}>
                            {failed}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${formatPercentage(daySuccessRate)}%`}
                            color={daySuccessRate >= 95 ? 'success' : daySuccessRate >= 85 ? 'warning' : 'error'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog de Webhooks Falhados */}
      <Dialog open={showFailedDialog} onClose={() => setShowFailedDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Webhooks Falhados ({failedWebhooks.length})
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Job ID</TableCell>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Evento</TableCell>
                  <TableCell>Tentativas</TableCell>
                  <TableCell>Criado em</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {failedWebhooks.map((webhook, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {webhook.id?.substring(0, 8)}...
                      </Typography>
                    </TableCell>
                    <TableCell>{webhook.order_id}</TableCell>
                    <TableCell>
                      <Chip label={webhook.payload?.event || 'N/A'} size="small" />
                    </TableCell>
                    <TableCell>{webhook.attempts}</TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {new Date(webhook.created_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Reprocessar webhook">
                        <IconButton 
                          size="small" 
                          onClick={() => retryWebhook(webhook.id)}
                          color="primary"
                        >
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
            <Button
              onClick={retryAllFailed}
              color="primary"
              variant="contained"
              disabled={failedWebhooks.length === 0 || bulkRetrying}
            >
              {bulkRetrying ? 'Reprocessando...' : 'Reprocessar todos'}
            </Button>
          <Button onClick={() => setShowFailedDialog(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WebhookMonitoring;